import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import { bootstrapActionTypes, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { getIsUserProgramQuotes, getOfferPrice, getSelectedPaymentProfitDetails } from '../selectors/offerRedux';
import { RECALCULATE_PROFIT_DETAILS } from '../actionTypes/profitActionTypes';
import { shouldSuppressSystemInitiatedPricing } from '../selectors/pendingDealerReviewSelectors';
import { getVppEstimatedPriceTotals } from '../selectors/vehicleProtectionSelectors';
import { getApplicationName } from '../selectors/offerSelectors';
import { DARWIN_APPLICATION_NAME } from '../constants';

const PRODUCT_PRICE_TOLERANCE = 1;

/**
 * Middleware to handle profit details calculation on deal load.
 *
 * When a fresh deal is loaded into MV and contains no profit details,
 * this middleware will trigger a recalculation to ensure profit details are presented.
 *
 * It also recalculates when the products on the deal price higher than the persisted profit accounts
 * for. The profit block is only ever produced by a payment response, so products added out of band
 * leave the block as a correct answer to a stale question. The comparison is deliberately
 * one-directional: persisted totals above the current product prices are legitimate, since profit
 * also covers products the vehicle protection state does not hold.
 */
export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            const state = store.getState();
            if (shouldSuppressSystemInitiatedPricing(state)) {
                break;
            }

            // Do not recalculate profit for Darwin deals
            if (
                featureToggleSelectors.isSkipDarwinProfitRecalcEnabled(state) &&
                getApplicationName(state) === DARWIN_APPLICATION_NAME
            ) {
                break;
            }

            const profitDetails = getSelectedPaymentProfitDetails(state);
            const currentOfferPrice = getOfferPrice(state);

            const isUserProgramQuotes = getIsUserProgramQuotes(state);

            const hasFrontEndProfit = profitDetails?.frontEndProfit != null;
            const hasBackEndProfit = profitDetails?.backEndProfit != null;
            const hasReserveProfit = profitDetails?.reserveProfit != null;
            const hasTotalProfit = isUserProgramQuotes ? hasFrontEndProfit && hasBackEndProfit : profitDetails?.totalProfit != null;

            const hasProfitDetails = [hasFrontEndProfit, hasBackEndProfit, hasReserveProfit, hasTotalProfit].every(Boolean);

            const productPrices = getVppEstimatedPriceTotals(state);
            const isUnaccountedFor = (productPrice: number, profitPrice: number) =>
                productPrice - profitPrice >= PRODUCT_PRICE_TOLERANCE;

            const hasUnaccountedProductPrices =
                isUnaccountedFor(productPrices.backEnd, profitDetails?.totalBackEndProtectionProductsPrice ?? 0) ||
                isUnaccountedFor(productPrices.frontEnd, profitDetails?.totalFrontEndProtectionProductsPrice ?? 0);

            if ((!hasProfitDetails || hasUnaccountedProductPrices) && currentOfferPrice) {
                store.dispatch({
                    type: RECALCULATE_PROFIT_DETAILS,
                    meta: {
                        middleware: {
                            payment: true
                        },
                        ...(featureToggleSelectors.isLoadEnrichmentRebaselineEnabled(state) && {
                            rebaselineUnsavedChangesWhenClean: true
                        })
                    }
                });
            }
            break;
        }
    }
};
